import { useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Modal, ModalName } from "pages/setting/components/Modal.styled";
import { ButtonList } from "pages/setting/components/Sections/components/ButtonList";
import { ProfileForm } from "pages/setting/components/Sections/NormalSetting/Modals/ProfileSettingModal/components/ProfileForm";
import { getUserInfo } from "shared/api/AuthApi";
import { PatchUserInfo } from "shared/api/SettingApi";
import {
  alertDialog,
  confirmDialog,
  confirmLeaveIfDirty,
} from "shared/ui/feedback";

/**
 * '프로필 설정' 모달 컴포넌트
 */
export const ProfileSettingModal = ({ isModalOpen, setIsModalOpen }) => {
  const queryClient = useQueryClient();
  const [userInfo, setUserInfo] = useState([]);
  const regex = useMemo(() => /^[a-zA-Z0-9ㄱ-ㅎㅏ-ㅣ가-힣\s]+$/, []);

  const [name, setName] = useState("");
  const [position, setPosition] = useState(null);
  const [department, setDepartment] = useState(null);
  const [departmentBtnClicked, setDepartmentBtnClicked] = useState([
    false,
    false,
    false,
    false,
  ]);
  const [originalName, setOriginalName] = useState("");
  const [originalPosition, setOriginalPosition] = useState(null);
  const [originalDepartment, setOriginalDepartment] = useState(null);
  const [disabled, setDisabled] = useState(true);

  const onChangeName = (event) => {
    setName(event.target.value);
  };

  const onChangePosition = (event) => {
    setPosition(event.target.value);
  };

  const handleDepartmentBtnClick = (index) => {
    const updatedDepartmentBtnClicked = departmentBtnClicked.map(
      (_, i) => i === index,
    );
    setDepartmentBtnClicked(updatedDepartmentBtnClicked);
    if (index === 0) {
      setDepartment("총학생회");
    } else if (index === 1) {
      setDepartment("총동연");
    } else if (index === 2) {
      setDepartment("중선관위");
    } else {
      setDepartment("예심위");
    }
  };

  const openCancelConfirm = async () => {
    const hasChanges =
      name !== originalName ||
      department !== originalDepartment ||
      position !== originalPosition;
    const shouldProceed = await confirmLeaveIfDirty(
      hasChanges,
      "취소 시 수정한 내용들이 반영되지 않습니다.\n그래도 취소하시겠습니까?",
    );

    if (shouldProceed) {
      setDisabled(true);
      setName(originalName);
      setDepartment(originalDepartment);
      setPosition(originalPosition);
      if (originalDepartment === "총학생회") {
        setDepartmentBtnClicked([true, false, false, false]);
      } else if (originalDepartment === "총동연") {
        setDepartmentBtnClicked([false, true, false, false]);
      } else if (originalDepartment === "중선관위") {
        setDepartmentBtnClicked([false, false, true, false]);
      } else if (originalDepartment === "예심위") {
        setDepartmentBtnClicked([false, false, false, true]);
      } else {
        setDepartmentBtnClicked([false, false, false, false]);
      }
      setIsModalOpen(false);
    }
  };

  const openConfirm = async () => {
    const shouldProceed = await confirmDialog(
      "회원 정보를 수정하시겠습니까?\n회원님의 정보가 수정하신 정보들로 변경됩니다.",
    );

    if (shouldProceed) {
      try {
        await PatchUserInfo(name, department, position);

        // 페이지 리로드 대신, 방금 저장한 값을 클라이언트 상태에 반영한다.
        // 기준값(original*)을 갱신해 두어야 다음에 모달을 다시 열거나
        // '취소' 시 되돌릴 때 최신 값을 기준으로 동작한다.
        setUserInfo((prev) => ({
          ...prev,
          name,
          admin: { ...prev?.admin, department, position },
        }));
        setOriginalName(name);
        setOriginalDepartment(department);
        setOriginalPosition(position);
        setDisabled(true);

        // 헤더·사이드바 등 useMyProfile(["myProfile"]) 소비자도 최신 이름으로
        // 갱신되도록 캐시를 다시 가져오게 한다.
        queryClient.invalidateQueries({ queryKey: ["myProfile"] });

        setIsModalOpen(false);
      } catch (error) {
        alertDialog("프로필 수정에 실패했습니다. 잠시 후 다시 시도해주세요.");
      }
    }
  };

  useEffect(() => {
    getUserInfo()
      .then((data) => {
        setUserInfo(data);
        setOriginalName(data.name);
        setOriginalPosition(data.admin?.position);
        setName(data.name);
        // 총학 관리자(auth 3)·마스터(4)는 소속이 '총학생회'로 고정된다.
        // 일반 관리자(auth 2)만 산하 자치기구(총동연/중선관위/예심위)를 선택한다.
        if (Number(data.auth) >= 3) {
          setOriginalDepartment("총학생회");
          setDepartment("총학생회");
          setDepartmentBtnClicked([true, false, false, false]);
        } else {
          setOriginalDepartment(data.admin?.department);
          if (data.admin?.department === "총동연") {
            setDepartmentBtnClicked([false, true, false, false]);
            setDepartment("총동연");
          } else if (data.admin?.department === "중선관위") {
            setDepartmentBtnClicked([false, false, true, false]);
            setDepartment("중선관위");
          } else if (data.admin?.department === "예심위") {
            setDepartmentBtnClicked([false, false, false, true]);
            setDepartment("예심위");
          }
        }
        setPosition(data.admin?.position);
      })
      .catch((err) => {
      });
  }, []);

  useEffect(() => {
    if (
      ((name !== originalName && name !== "") ||
        (department !== originalDepartment && department !== "") ||
        (position !== originalPosition && position !== "")) &&
      regex.test(name) &&
      regex.test(position)
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [
    name,
    department,
    position,
    originalName,
    originalDepartment,
    originalPosition,
    regex,
  ]);

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={() => setIsModalOpen(false)}
      style={{ overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
    >
      <ModalName>프로필 설정</ModalName>

      {/* 프로필 설정 폼 */}
      <ProfileForm
        userInfo={userInfo}
        name={name}
        onChangeName={onChangeName}
        departmentBtnClicked={departmentBtnClicked}
        handleDepartmentBtnClick={handleDepartmentBtnClick}
        position={position}
        onChangePosition={onChangePosition}
      />

      {/* 버튼 모음 */}
      <ButtonList
        closeModal={openCancelConfirm}
        openConfirm={openConfirm}
        disabled={disabled}
        confirmMessage="수정"
      />
    </Modal>
  );
};
